import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Canonical production domain. Always use this - never window.location.origin -
// for canonical/og:url/hreflang tags. The site is reachable at both
// avagyanlaw.com and www.avagyanlaw.com (no server-side redirect between them
// yet), and building these tags from window.location.origin made them
// self-referencing on whichever host served the request - so on
// www.avagyanlaw.com the canonical tag pointed back at www, telling Google
// nothing about which host is authoritative. Every canonical/OG/hreflang tag
// must point at this one fixed origin regardless of which host the page was
// actually loaded from.
export const CANONICAL_ORIGIN = 'https://avagyanlaw.com';

const SEOUpdater = ({ page, noindex = false }) => {
  const { t, i18n } = useTranslation(page);

  useEffect(() => {
    const origin = CANONICAL_ORIGIN;
    const pathname = window.location.pathname;

    // ✅ Set <html lang="">
    document.documentElement.lang = i18n.language;

    // ✅ Title
    document.title = t('helmetTitle');

    // Helper
    const setMetaTag = (selector, attr, name, content) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Standard meta
    setMetaTag('meta[name="description"]', 'name', 'description', t('helmetDescription'));
    // Only set/keep a keywords tag when the page actually defines one - most
    // page namespaces don't have a helmetKeywords key, and without this guard
    // i18next's missing-key fallback returns the literal string "helmetKeywords"
    // which was shipping as the tag's content on every one of those pages.
    const keywords = t('helmetKeywords', { defaultValue: '' });
    if (keywords) {
      setMetaTag('meta[name="keywords"]', 'name', 'keywords', keywords);
    } else {
      document.querySelector('meta[name="keywords"]')?.remove();
    }
    setMetaTag('meta[name="language"]', 'name', 'language', i18n.language);

    // OG
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', t('ogTitle'));
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', t('ogDescription'));
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', origin + pathname);

    // Twitter
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', t('twitterTitle'));
    setMetaTag(
      'meta[name="twitter:description"]',
      'name',
      'twitter:description',
      t('twitterDescription'),
    );
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');

    // =========================
    // ✅ FIXED CANONICAL
    // =========================
    let cleanPath = pathname;

    // Force root to /en/
    if (cleanPath === '/') {
      cleanPath = '/en/';
    }

    const canonicalUrl = origin + cleanPath;

    // Error pages (404) must not be indexed or canonicalized - a canonical on
    // a 404 tells Google it's a real page, and "index" invites a soft-404.
    setMetaTag('meta[name="robots"]', 'name', 'robots', noindex ? 'noindex, follow' : 'index, follow');
    if (noindex) {
      document.querySelector("link[rel='canonical']")?.remove();
      document.querySelectorAll("link[rel='alternate']").forEach((el) => el.remove());
      return;
    }

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // =========================
    // ✅ ADD HREFLANG (CRITICAL)
    // =========================

    const languages = ['en', 'ru', 'hy', 'es'];

    // Remove old hreflang tags first
    document.querySelectorAll("link[rel='alternate']").forEach(el => el.remove());

    // Remove current language from path
    const pathWithoutLang = cleanPath.replace(/^\/(en|ru|hy|es)/, '');

    languages.forEach(lang => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', `${origin}/${lang}${pathWithoutLang}`);
      document.head.appendChild(link);
    });

    // x-default
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    // x-default = the English version of THIS page (was hardcoded to the
    // homepage, which pointed every page's x-default at /en/ after hydration).
    xDefault.setAttribute('href', `${origin}/en${pathWithoutLang}`);
    document.head.appendChild(xDefault);
  }, [i18n.language, t, noindex]);

  return null;
};

export default SEOUpdater;
